// Règles de calcul des frais de déplacement (page Tarifs).
// Stockées dans app_settings (clé `tarifs.regles_deplacement`) plutôt qu'en
// variable d'environnement : ce sont des règles de gestion, modifiables sans
// redéploiement par qui a le droit de modifier les grilles.

import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Moon, Save, Loader2 } from 'lucide-react';
import { getSetting, updateSetting } from '../services/api';
import { REGLES_CLE, lireRegles } from '../lib/fraisDeplacement';

const champ = { padding: '6px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 };
const ligne = { display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', fontSize: 13, color: '#475569', flexWrap: 'wrap' };

export default function ReglesDeplacement({ canEdit }) {
  const [regles, setRegles] = useState(null);
  const [initiales, setInitiales] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSetting(REGLES_CLE)
      .then((v) => { const r = lireRegles(v); setRegles(r); setInitiales(r); })
      .catch(() => { const r = lireRegles(null); setRegles(r); setInitiales(r); });
  }, []);

  if (!regles) return null;
  const modifie = JSON.stringify(regles) !== JSON.stringify(initiales);
  const set = (k, v) => setRegles((r) => ({ ...r, [k]: v }));

  async function enregistrer() {
    setSaving(true);
    try {
      await updateSetting(REGLES_CLE, regles);
      setInitiales(regles);
      toast.success('Règles de déplacement enregistrées');
    } catch (e) {
      toast.error(e?.response?.status === 403 ? 'Droit insuffisant' : "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  }

  return (
    <section style={{ marginTop: 24, background: 'white', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Moon size={16} color="#92400e" />
        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Règles de calcul des déplacements</h2>
      </div>
      <p style={{ margin: '0 0 10px 0', fontSize: 12, color: '#64748b' }}>
        Utilisées par le calculateur de frais de la fiche antenne (bouton « Frais de déplacement »).
        Un aller-retour coûte le <strong>forfait minimum kilométrique</strong> de la grille, qui couvre
        les premiers km ci-dessous, puis le <strong>barème kilométrique</strong> au-delà ; l'aller (livraison)
        et le retour (reprise) en coûtent chacun la moitié. Quand le départ
        tombe dans une plage majorée, le forfait majoré de la grille s'applique à la place, s'il existe.
      </p>

      <div style={ligne}>
        <span style={{ minWidth: 200 }}>Km inclus dans le forfait</span>
        <input type="number" min="0" step="1" value={regles.km_inclus} disabled={!canEdit}
          onChange={(e) => set('km_inclus', Math.max(0, parseInt(e.target.value, 10) || 0))}
          style={{ ...champ, width: 80 }} />
        <span>km, comptés sur</span>
        <select value={regles.seuil_sur} disabled={!canEdit} onChange={(e) => set('seuil_sur', e.target.value)} style={champ}>
          <option value="ar">la distance aller-retour</option>
          <option value="aller">la distance aller simple</option>
        </select>
      </div>
      <div style={ligne}>
        <span style={{ minWidth: 200 }}>Nuit (majorée)</span>
        <span>de</span>
        <input type="time" value={regles.nuit_debut} disabled={!canEdit} onChange={(e) => set('nuit_debut', e.target.value)} style={champ} />
        <span>à</span>
        <input type="time" value={regles.nuit_fin} disabled={!canEdit} onChange={(e) => set('nuit_fin', e.target.value)} style={champ} />
        <span style={{ fontSize: 11, color: '#94a3b8' }}>(mêmes heures = pas de majoration de nuit)</span>
      </div>
      <div style={ligne}>
        <span style={{ minWidth: 200 }}>Jours majorés</span>
        {[
          ['dimanche', 'Dimanche'],
          ['feries', 'Jours fériés'],
          ['samedi', 'Samedi'],
        ].map(([k, l]) => (
          <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={!!regles[k]} disabled={!canEdit} onChange={(e) => set(k, e.target.checked)} />
            {l}
          </label>
        ))}
      </div>

      {canEdit && (
        <div style={{ marginTop: 10 }}>
          <button
            type="button"
            onClick={enregistrer}
            disabled={!modifie || saving}
            data-perm="antennes:tarifs_standard.edit"
            style={{
              padding: '7px 14px', fontSize: 13, fontWeight: 600, borderRadius: 8, border: 'none',
              background: !modifie || saving ? '#cbd5e1' : '#4f46e5', color: 'white',
              cursor: !modifie || saving ? 'default' : 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            {saving ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Save size={14} />}
            Enregistrer les règles
          </button>
        </div>
      )}
    </section>
  );
}
